/* eslint-disable react/prop-types */
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useAuth from "@/hooks/useAuth";
import {
  createNewMixAndMatch,
  updateExistingMixAndMatch,
  clearMixAndMatchMessages,
} from "@/redux/slice/menu-manage-slice/mix-and-match-slice";
import {
  Plus,
  Trash2,
  X,
  Layers,
  ArrowLeft,
  Save,
  CheckCircle2,
} from "lucide-react";

// Validation Schema
export const mixAndMatchSchema = z.object({
  name: z.string().min(1, "Name is required"),
  max_choose: z
    .preprocess(
      (val) => (val === "" || val == null ? null : Number(val)),
      z.number({ invalid_type_error: "Max choices must be a number" }).min(1, "Max choices must be at least 1")
    ),
  vat: z
    .preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({ invalid_type_error: "VAT must be a number" }).min(0).max(100)
    )
    .optional(),
  status: z.enum(["active", "inactive", "archived", "draft"]).default("active"),
  categories: z
    .array(
      z.object({
        id: z.number().optional(),
        category_name: z.string().min(1, "Category name is required"),
        price: z
          .preprocess(
            (val) => (val === "" || val == null ? 0 : Number(val)),
            z.number({ invalid_type_error: "Price must be a number" }).min(0)
          ),
        items: z
          .array(
            z.union([
              z.string().min(1),
              z.object({
                id: z.number().optional(),
                name: z.string().min(1),
              }),
            ])
          )
          .min(1, "At least one item is required in this category"),
      })
    )
    .min(1, "At least one category is required"),
});

function CategoryCardItem({
  catIndex,
  form,
  removeCategory,
  canRemove,
}) {
  const [itemInput, setItemInput] = useState("");

  const items = form.watch(`categories.${catIndex}.items`) || [];

  const handleAddItem = () => {
    const trimmed = itemInput.trim();
    if (!trimmed) return;

    const currentItems = form.getValues(`categories.${catIndex}.items`) || [];
    // Check if duplicate
    const exists = currentItems.some(
      (it) => (typeof it === "string" ? it : it?.name)?.toLowerCase() === trimmed.toLowerCase()
    );

    if (exists) {
      toast.error(`"${trimmed}" is already in this category!`);
      return;
    }

    form.setValue(`categories.${catIndex}.items`, [
      ...currentItems,
      trimmed,
    ]);
    setItemInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddItem();
    }
  };

  const handleRemoveItem = (itemIdx) => {
    const currentItems = form.getValues(`categories.${catIndex}.items`) || [];
    const updated = currentItems.filter((_, idx) => idx !== itemIdx);
    form.setValue(`categories.${catIndex}.items`, updated);
  };

  return (
    <Card className="bg-cblack3 border border-cblack6 relative overflow-hidden">
      <div className="flex justify-between items-center bg-cblack7 px-4 py-3 border-b border-cblack6">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold px-2 py-0.5 rounded bg-cyellow text-black">
            Category #{catIndex + 1}
          </span>
          <span className="text-sm font-medium text-cwhite">
            {form.watch(`categories.${catIndex}.category_name`) || "New Category"}
          </span>
        </div>

        {canRemove && (
          <button
            type="button"
            onClick={() => removeCategory(catIndex)}
            className="text-cred hover:text-cred/80 transition-colors p-1"
            title="Remove Category"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      <CardContent className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name={`categories.${catIndex}.category_name`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-cwhite2 text-xs">
                  Category Name <span className="text-cred">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="e.g. Cat 01, Starter, Drink"
                    className="bg-cblack2 border-cblack6 text-cwhite text-sm"
                  />
                </FormControl>
                <FormMessage className="text-cred text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={`categories.${catIndex}.price`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-cwhite2 text-xs">
                  Price ($) <span className="text-cred">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    {...field}
                    placeholder="0.00"
                    className="bg-cblack2 border-cblack6 text-cwhite text-sm"
                  />
                </FormControl>
                <FormMessage className="text-cred text-xs" />
              </FormItem>
            )}
          />
        </div>

        {/* Nested Items Tag Builder */}
        <div className="space-y-2">
          <FormLabel className="text-cwhite2 text-xs">
            Category Items <span className="text-cred">*</span>
          </FormLabel>
          <div className="flex gap-2">
            <Input
              value={itemInput}
              onChange={(e) => setItemInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type item name & press Enter or click Add (e.g. item 001)..."
              className="bg-cblack2 border-cblack6 text-cwhite text-sm"
            />
            <Button
              type="button"
              onClick={handleAddItem}
              className="bg-cblack7 text-cwhite hover:bg-cblack6 text-xs px-4"
            >
              <Plus size={14} className="mr-1" /> Add
            </Button>
          </div>

          {/* Items badges */}
          <div className="flex flex-wrap gap-2 mt-2 min-h-[38px] p-2 bg-cblack2 rounded-md border border-cblack6">
            {items && items.length > 0 ? (
              items.map((item, itemIdx) => {
                const itemName =
                  typeof item === "string" ? item : item?.name;
                return (
                  <span
                    key={item?.id || itemIdx}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-cblack7 text-cwhite border border-cblack6"
                  >
                    {itemName}
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(itemIdx)}
                      className="text-cwhite3 hover:text-cred transition-colors"
                    >
                      <X size={12} />
                    </button>
                  </span>
                );
              })
            ) : (
              <span className="text-xs text-cwhite3 italic flex items-center">
                No items added yet. Add items for this category above.
              </span>
            )}
          </div>

          {form.formState.errors?.categories?.[catIndex]?.items && (
            <p className="text-cred text-xs">
              {form.formState.errors.categories[catIndex].items.message}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CreateMixAndMatch() {
  const navigate = useNavigate();
  const { type } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const item = location.state;

  const { createLoading, updateLoading } = useSelector(
    (state) => state.menuManage.mixAndMatch
  );

  const form = useForm({
    resolver: zodResolver(mixAndMatchSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      max_choose: 3,
      vat: 20,
      status: "active",
      categories: [
        {
          category_name: "Cat 01",
          price: 0,
          items: [],
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "categories",
  });

  useEffect(() => {
    if (type === "update" && item) {
      const formattedCategories =
        item.categories?.map((cat) => ({
          id: cat.id,
          category_name: cat.category_name,
          price: cat.price,
          items: cat.items || [],
        })) || [];

      form.reset({
        name: item.name || "",
        max_choose: item.max_choose ?? 3,
        vat: item.vat ?? 20,
        status: item.status || "active",
        categories:
          formattedCategories.length > 0
            ? formattedCategories
            : [
                {
                  category_name: "Cat 01",
                  price: 0,
                  items: [],
                },
              ],
      });
    }
  }, [type, item, form]);

  const onSubmit = async (values, submitStatus = null) => {
    try {
      const finalStatus = submitStatus || values.status || "active";

      // Structure payload for create vs update
      const formattedCategories = values.categories.map((cat) => {
        const catPayload = {
          category_name: cat.category_name,
          price: Number(cat.price),
          items: cat.items.map((it) => {
            if (typeof it === "string") return it;
            return it.id ? { id: it.id, name: it.name } : { name: it.name };
          }),
        };
        if (type === "update" && cat.id) {
          catPayload.id = cat.id;
        }
        return catPayload;
      });

      const payload = {
        restaurant_id: user?.restaurant_id || 1,
        name: values.name,
        max_choose: Number(values.max_choose),
        vat: Number(values.vat || 0),
        status: finalStatus,
        categories: formattedCategories,
      };

      if (type !== "update") {
        payload.client_id = user?.client_id || 1;
      }

      dispatch(clearMixAndMatchMessages());

      let res;
      if (type === "update") {
        res = await dispatch(
          updateExistingMixAndMatch({
            id: item?.id,
            data: payload,
          })
        );
      } else {
        res = await dispatch(createNewMixAndMatch(payload));
      }

      if (
        createNewMixAndMatch.fulfilled.match(res) ||
        updateExistingMixAndMatch.fulfilled.match(res)
      ) {
        toast.success(
          type === "update"
            ? "Mix & Match updated successfully!"
            : "Mix & Match created successfully!"
        );
        navigate("/backoffice/menu/mix-and-match");
      } else {
        toast.error(res?.payload || "Operation failed.");
      }
    } catch (err) {
      toast.error(err.message || "An error occurred.");
    }
  };

  const isSubmitting = createLoading || updateLoading;

  return (
    <Layout2
      pageName={{
        title: type === "update" ? "Update Mix & Match" : "Add New Mix & Match",
      }}
      paths={[
        { name: "Home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/overview" },
        { name: "Mix & Match", path: "/backoffice/menu/mix-and-match" },
        {
          name: type === "update" ? "Update Mix & Match" : "Create Mix & Match",
          path: `/backoffice/menu/mix-and-match/${type}`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <div className="container py-6 max-w-5xl mx-auto space-y-6">
        {/* Header Bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/backoffice/menu/mix-and-match")}
              className="p-2 rounded-lg bg-cblack3 border border-cblack6 text-cwhite2 hover:text-cwhite transition-colors"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-cwhite">
                {type === "update" ? "Update Mix & Match Item" : "Create Mix & Match Item"}
              </h1>
              <p className="text-xs text-cwhite3">
                Configure mix and match item details, price categories, and choices
              </p>
            </div>
          </div>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((val) => onSubmit(val))}
            className="space-y-6"
          >
            {/* General Info Card */}
            <Card className="bg-cblack2 border border-cblack6">
              <div className="px-5 py-4 border-b border-cblack6 flex items-center gap-2">
                <Layers size={18} className="text-cyellow" />
                <h3 className="font-semibold text-cwhite text-base">
                  General Information
                </h3>
              </div>
              <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="lg:col-span-2">
                      <FormLabel className="text-cwhite2 text-xs">
                        Mix & Match Name <span className="text-cred">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g. Lunch Mix N Match, 3 for 20 Deal"
                          className="bg-cblack3 border-cblack6 text-cwhite"
                        />
                      </FormControl>
                      <FormMessage className="text-cred text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="max_choose"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-cwhite2 text-xs">
                        Max Choices <span className="text-cred">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          placeholder="3"
                          className="bg-cblack3 border-cblack6 text-cwhite"
                        />
                      </FormControl>
                      <FormMessage className="text-cred text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="vat"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-cwhite2 text-xs">
                        VAT (%)
                      </FormLabel>
                      <Select
                        onValueChange={(val) => field.onChange(Number(val))}
                        value={field.value !== undefined && field.value !== null ? String(field.value) : "20"}
                      >
                        <FormControl>
                          <SelectTrigger className="bg-cblack3 border-cblack6 text-cwhite">
                            <SelectValue placeholder="Select VAT" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-cblack3 border-cblack6 text-cwhite">
                          <SelectItem value="0">0%</SelectItem>
                          <SelectItem value="20">20%</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage className="text-cred text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-cwhite2 text-xs">
                        Status
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="bg-cblack3 border-cblack6 text-cwhite">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-cblack3 border-cblack6 text-cwhite">
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="draft">Draft</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage className="text-cred text-xs" />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Categories & Items Section */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-cwhite">
                    Categories & Choices
                  </h3>
                  <p className="text-xs text-cwhite3">
                    Add categories and provide the list of items customers can pick from
                  </p>
                </div>

                <Button
                  type="button"
                  onClick={() =>
                    append({
                      category_name: `Cat 0${fields.length + 1}`,
                      price: 0,
                      items: [],
                    })
                  }
                  className="bg-cblack3 border border-cblack6 text-cyellow hover:bg-cblack7 text-xs flex items-center gap-1.5"
                >
                  <Plus size={16} /> Add Category
                </Button>
              </div>

              {fields.map((field, index) => (
                <CategoryCardItem
                  key={field.id}
                  catIndex={index}
                  form={form}
                  removeCategory={remove}
                  canRemove={fields.length > 1}
                />
              ))}

              {form.formState.errors?.categories?.message && (
                <p className="text-cred text-xs">
                  {form.formState.errors.categories.message}
                </p>
              )}
            </div>

            {/* Action Buttons Footer */}
            <div className="flex justify-between items-center pt-4 border-t border-cblack6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/backoffice/menu/mix-and-match")}
                className="border-cblack6 text-cwhite2 hover:bg-cblack3"
              >
                Cancel
              </Button>

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={() => form.handleSubmit((val) => onSubmit(val, "draft"))()}
                  className="border-cblack6 text-cwhite hover:bg-cblack3"
                >
                  <Save size={16} className="mr-1.5 text-cwhite3" /> Save as Draft
                </Button>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-cyellow text-black hover:bg-cyellow/90 font-medium px-6"
                >
                  <CheckCircle2 size={16} className="mr-1.5" />
                  {isSubmitting
                    ? "Submitting..."
                    : type === "update"
                    ? "Update Mix & Match"
                    : "Create Mix & Match"}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </Layout2>
  );
}

export default CreateMixAndMatch;
